import React, { useEffect, useRef, useState } from 'react';
import firestoreService from '../../shared/services/firestoreService';
import { prepareImageForUpload } from '../../shared/imageResize';
import { MENU_PHOTO_MAX_DIMENSION, menuPhotoUrl } from '../../shared/menuPhotos';
import { userMessage } from '../../shared/userFacingError';
import type { MenuItem } from '../../shared/types';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { CameraIcon, UtensilsIcon } from './icons/UiIcons';
import {
    ACCEPTED_IMAGE_TYPES,
    StatusRegion,
    createTaskStore,
    useMountedRef,
    useTask,
} from './RestaurantPhotosEditor';

/**
 * Photos des plats de « Ma Carte » : vignette, boutons « Ajouter une photo /
 * Remplacer / Retirer » (liste de la carte et fenêtre d'ajout / modification).
 * Chaque envoi est enregistré dès qu'il est terminé (Storage + transaction sur
 * le seul plat concerné, cf. firestoreService « CARTE »).
 */

/** save : enregistrement du plat (fenêtre) ; prepare / upload / remove : sa photo. */
type PhotoBusy = 'save' | 'prepare' | 'upload' | 'remove';

/** Échec d'une opération photo, affiché près du plat avec « Réessayer » si possible. */
interface ItemError {
    message: string;
    retry?: { kind: 'upload'; image: Blob } | { kind: 'remove' };
}

// ─────────────────────────────────────────────────────────────────────────────
// État des opérations au niveau du module (comme la vitrine) : une opération
// continue si l'éditeur est démonté (changement d'onglet, navigation), le retour
// sur la page la montre en cours (pas de double envoi) avec son erreur éventuelle ;
// fermer l'onglet du navigateur demande confirmation tant qu'elle tourne.
// ─────────────────────────────────────────────────────────────────────────────

interface MenuPhotoTasks {
    byItem: Record<string, PhotoBusy>;
    errors: Record<string, ItemError>;
    /** Dernière annonce (succès, informations) pour les lecteurs d'écran ; les erreurs ont leur role="alert". */
    status: string;
}

const menuPhotoTask = createTaskStore<MenuPhotoTasks>(
    { busy: { byItem: {}, errors: {}, status: '' }, error: null },
    (busy) => Object.keys(busy.byItem).length > 0,
);

function updateTasks(update: (tasks: MenuPhotoTasks) => Partial<MenuPhotoTasks>) {
    const current = menuPhotoTask.get().busy;
    menuPhotoTask.set({ busy: { ...current, ...update(current) } });
}

function setItemBusy(itemId: string, busy: PhotoBusy | null) {
    updateTasks(({ byItem }) => {
        const next = { ...byItem };
        if (busy) next[itemId] = busy;
        else delete next[itemId];
        return { byItem: next };
    });
}

function setItemError(itemId: string, error: ItemError | null) {
    updateTasks(({ errors }) => {
        const next = { ...errors };
        if (error) next[itemId] = error;
        else delete next[itemId];
        return { errors: next };
    });
}

/** Annonce polie (role="status") dans la page « Ma Carte ». */
export function announceMenuStatus(text: string) {
    updateTasks(() => ({ status: text }));
}

/** Nom affichable d'un plat (un plat ancien peut ne pas en avoir). */
export const dishLabel = (item: Partial<MenuItem>) => `« ${String(item.name ?? '').trim() || 'Plat sans nom'} »`;

/** true si une opération est en cours sur ce plat. */
export const isMenuPhotoBusy = (itemId: string) => menuPhotoTask.get().busy.byItem[itemId] !== undefined;

/** Opération en cours sur ce plat (pour griser ses boutons). */
export function useMenuPhotoBusy(itemId: string | undefined): PhotoBusy | undefined {
    const { busy } = useTask(menuPhotoTask);
    return itemId ? busy.byItem[itemId] : undefined;
}

/** Plats ayant une opération en cours (id → opération). */
export function useMenuPhotoTasks(): Readonly<Record<string, PhotoBusy>> {
    return useTask(menuPhotoTask).busy.byItem;
}

/**
 * Carte enregistrée renvoyée par le service et plat concerné : l'éditeur ne
 * reprend que ce plat. Doit lui-même ignorer l'appel s'il est démonté.
 */
type OnSaved = (menu: MenuItem[], itemId: string) => void;

/** Envoie la photo (déjà préparée, ou à préparer si c'est un File) d'un plat enregistré. */
export async function uploadMenuItemPhoto(
    restaurantId: number | string,
    item: MenuItem,
    source: Blob | File,
    onSaved?: OnSaved,
): Promise<boolean> {
    if (isMenuPhotoBusy(item.id)) return false;
    setItemError(item.id, null);
    let image: Blob | null = source instanceof File ? null : source;
    try {
        if (!image) {
            setItemBusy(item.id, 'prepare');
            image = await prepareImageForUpload(source as File, MENU_PHOTO_MAX_DIMENSION);
        }
        setItemBusy(item.id, 'upload');
        const menu = await firestoreService.setMenuItemPhoto(restaurantId, item.id, image);
        onSaved?.(menu, item.id);
        setItemBusy(item.id, null);
        announceMenuStatus(`Photo de ${dishLabel(item)} enregistrée.`);
        return true;
    } catch (err) {
        setItemBusy(item.id, null);
        setItemError(item.id, {
            message: `${dishLabel(item)} : ${userMessage(err, "L'envoi de la photo a échoué. Réessayez.")}`,
            // Photo illisible (préparation) : réessayer ne servirait à rien.
            retry: image ? { kind: 'upload', image } : undefined,
        });
        return false;
    }
}

/** Retire la photo d'un plat (document puis fichier). */
export async function removeMenuItemPhoto(restaurantId: number | string, item: MenuItem, onSaved?: OnSaved): Promise<boolean> {
    if (isMenuPhotoBusy(item.id)) return false;
    setItemError(item.id, null);
    setItemBusy(item.id, 'remove');
    try {
        const menu = await firestoreService.removeMenuItemPhoto(restaurantId, item.id);
        onSaved?.(menu, item.id);
        setItemBusy(item.id, null);
        announceMenuStatus(`Photo de ${dishLabel(item)} retirée.`);
        return true;
    } catch (err) {
        setItemBusy(item.id, null);
        setItemError(item.id, {
            message: `${dishLabel(item)} : ${userMessage(err, "La photo n'a pas pu être retirée. Réessayez.")}`,
            retry: { kind: 'remove' },
        });
        return false;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fenêtre « Nouveau plat » / « Modifier le plat » : la photo choisie est
// préparée tout de suite (erreurs visibles dans la fenêtre) mais n'est envoyée
// qu'à la confirmation, après l'enregistrement du plat ; « Annuler » n'envoie rien.
// ─────────────────────────────────────────────────────────────────────────────

export type MenuPhotoDraft =
    | { kind: 'keep' }
    | { kind: 'set'; image: Blob; previewUrl: string }
    | { kind: 'remove' };

/**
 * Enregistre le plat (transaction sur ce seul plat) puis applique la photo
 * choisie. Indépendant du composant : si l'on quitte la page pendant
 * l'enregistrement, la photo est quand même envoyée (et fermer l'onglet du
 * navigateur demande confirmation pendant toute l'opération).
 */
export async function saveMenuItemWithPhoto(
    restaurantId: number | string,
    item: MenuItem,
    draft: MenuPhotoDraft,
    onSaved: OnSaved,
): Promise<void> {
    setItemBusy(item.id, 'save');
    let menu: MenuItem[];
    try {
        menu = await firestoreService.saveMenuItem(restaurantId, item);
    } finally {
        setItemBusy(item.id, null);
    }
    onSaved(menu, item.id);
    const saved = menu.find((m) => m.id === item.id) ?? item;
    if (draft.kind === 'set') void uploadMenuItemPhoto(restaurantId, saved, draft.image, onSaved);
    if (draft.kind === 'remove' && menuPhotoUrl(restaurantId, saved)) void removeMenuItemPhoto(restaurantId, saved, onSaved);
}

// ─────────────────────────────────────────────────────────────────────────────
// Composants
// ─────────────────────────────────────────────────────────────────────────────

const busyLabel: Record<PhotoBusy, string> = {
    save: 'Enregistrement…',
    prepare: 'Préparation…',
    upload: 'Envoi…',
    remove: 'Retrait…',
};

/**
 * Haut de la page « Ma Carte » : annonces (role="status") et, sans annonce
 * supplémentaire, la liste des plats dont la photo n'a pas été enregistrée
 * (le détail et « Réessayer » sont près de chaque plat, parfois hors de vue).
 */
export const MenuPhotoFeedback: React.FC<{ menu: readonly MenuItem[] }> = ({ menu }) => {
    const { busy } = useTask(menuPhotoTask);
    const failed = menu.filter((item) => busy.errors[item.id]);
    const showError = (itemId: string) => {
        const block = document.querySelector<HTMLElement>(`[data-menu-photo-error="${CSS.escape(itemId)}"]`);
        block?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        block?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    };
    return (
        <>
            {failed.length > 0 && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{failed.length > 1 ? 'Ces photos n\'ont pas été enregistrées :' : 'Cette photo n\'a pas été enregistrée :'}</span>
                    {failed.map((item) => (
                        <button key={item.id} type="button" onClick={() => showError(item.id)} className="font-bold underline">
                            {dishLabel(item)}
                        </button>
                    ))}
                </div>
            )}
            <StatusRegion text={busy.status} />
        </>
    );
};

interface ThumbProps {
    restaurantId: number | string;
    item: MenuItem;
    /** Aperçu local (photo choisie, pas encore envoyée) prioritaire sur la photo enregistrée. */
    previewUrl?: string | null;
    /** true : la photo enregistrée ne doit pas être montrée (retrait demandé). */
    hidden?: boolean;
    className?: string;
}

/** Vignette du plat : photo enregistrée (URL Storage validée) ou icône neutre. */
export const MenuItemThumb: React.FC<ThumbProps> = ({ restaurantId, item, previewUrl, hidden, className = 'w-16 h-16' }) => {
    const busy = useMenuPhotoBusy(item.id);
    const saved = hidden ? null : menuPhotoUrl(restaurantId, item);
    const url = previewUrl ?? saved;
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    return (
        <div className={`relative ${className} shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 flex items-center justify-center shadow-inner`}>
            {url && failedUrl !== url ? (
                <img src={url} alt="" loading="lazy" onError={() => setFailedUrl(url)} className="w-full h-full object-cover" />
            ) : (
                <UtensilsIcon className="w-5 h-5" />
            )}
            {busy && busy !== 'save' && (
                <div className="absolute inset-0 bg-white/85 flex items-center justify-center">
                    <SpinnerIcon className="w-5 h-5" />
                </div>
            )}
        </div>
    );
};

const linkButton = 'text-xs font-bold rounded px-1 -mx-1 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed';

interface ActionsProps {
    restaurantId: number | string;
    item: MenuItem;
    onSaved: OnSaved;
}

/** Boutons photo d'un plat dans la liste de la carte (enregistrés immédiatement), et son erreur. */
export const MenuItemPhotoActions: React.FC<ActionsProps> = ({ restaurantId, item, onSaved }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const pickRef = useRef<HTMLButtonElement>(null);
    const removeRef = useRef<HTMLButtonElement>(null);
    const refocusPick = useRef(false);
    const mounted = useMountedRef();
    const { busy: tasks } = useTask(menuPhotoTask);
    const busy = tasks.byItem[item.id];
    const error = tasks.errors[item.id];
    const hasPhoto = menuPhotoUrl(restaurantId, item) !== null;
    const name = dishLabel(item);

    // « Retirer » disparaît avec la photo : le focus revient sur « Ajouter une photo ».
    useEffect(() => {
        if (!hasPhoto && refocusPick.current) {
            refocusPick.current = false;
            pickRef.current?.focus();
        }
    }, [hasPhoto]);

    const savedIfMounted: OnSaved = (menu, itemId) => { if (mounted.current) onSaved(menu, itemId); };

    const openPicker = () => {
        if (isMenuPhotoBusy(item.id)) return;
        inputRef.current?.click();
    };

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // permet de re-choisir le même fichier après une erreur
        if (file) void uploadMenuItemPhoto(restaurantId, item, file, savedIfMounted);
    };

    const handleRemove = async () => {
        if (isMenuPhotoBusy(item.id) || !window.confirm(`Retirer la photo de ${name} ?`)) return;
        refocusPick.current = document.activeElement === removeRef.current;
        const removed = await removeMenuItemPhoto(restaurantId, item, savedIfMounted);
        if (!removed) refocusPick.current = false; // le bouton reste : pas de focus à déplacer
    };

    const retry = () => {
        if (!error?.retry || isMenuPhotoBusy(item.id)) return;
        pickRef.current?.focus(); // le message (et ce bouton) disparaissent pendant le nouvel essai
        if (error.retry.kind === 'upload') void uploadMenuItemPhoto(restaurantId, item, error.retry.image, savedIfMounted);
        else void removeMenuItemPhoto(restaurantId, item, savedIfMounted); // retrait déjà confirmé
    };

    const dismiss = () => {
        setItemError(item.id, null);
        pickRef.current?.focus();
    };

    return (
        <div className="mt-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <button
                    ref={pickRef}
                    type="button"
                    onClick={openPicker}
                    aria-disabled={busy !== undefined}
                    aria-label={`${hasPhoto ? 'Remplacer la photo de' : 'Ajouter une photo à'} ${name}`}
                    className={`${linkButton} text-brand-primary hover:underline`}
                >
                    {hasPhoto ? 'Remplacer la photo' : '+ Ajouter une photo'}
                </button>
                {hasPhoto && (
                    <button
                        ref={removeRef}
                        type="button"
                        onClick={handleRemove}
                        aria-disabled={busy !== undefined}
                        aria-label={`Retirer la photo de ${name}`}
                        className={`${linkButton} text-red-600 hover:underline`}
                    >
                        Retirer
                    </button>
                )}
                {busy && <span className="text-xs text-gray-400" aria-hidden="true">{busyLabel[busy]}</span>}
            </div>
            {error && (
                <div data-menu-photo-error={item.id} className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p role="alert" className="text-xs text-red-600">{error.message}</p>
                    {error.retry && (
                        <button type="button" onClick={retry} aria-disabled={busy !== undefined} className={`${linkButton} text-brand-primary underline`}>
                            Réessayer
                        </button>
                    )}
                    <button type="button" onClick={dismiss} aria-label={`Fermer le message d'erreur de ${name}`} className={`${linkButton} text-gray-500 underline`}>
                        Fermer
                    </button>
                </div>
            )}
            <input ref={inputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} className="hidden" tabIndex={-1} onChange={handleFile} />
        </div>
    );
};

interface DraftFieldProps {
    restaurantId: number | string;
    /** Plat en cours d'édition (nouveau plat : identifiant déjà choisi, pas encore enregistré). */
    item: MenuItem;
    draft: MenuPhotoDraft;
    onDraftChange: (draft: MenuPhotoDraft) => void;
    /** Préparation de la photo en cours : la fenêtre ne doit pas être confirmée entre-temps. */
    onPreparingChange: (preparing: boolean) => void;
    /** Enregistrement du plat en cours : la photo ne peut plus changer. */
    disabled?: boolean;
}

export const MenuItemPhotoField: React.FC<DraftFieldProps> = ({ restaurantId, item, draft, onDraftChange, onPreparingChange, disabled = false }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const pickRef = useRef<HTMLButtonElement>(null);
    const removeRef = useRef<HTMLButtonElement>(null);
    const refocusPick = useRef(false);
    const mounted = useMountedRef();
    const [preparing, setPreparing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState('');
    const busy = useMenuPhotoBusy(item.id);
    const locked = disabled || preparing || busy !== undefined;

    const previewUrl = draft.kind === 'set' ? draft.previewUrl : null;
    const savedUrl = menuPhotoUrl(restaurantId, item);
    const shown = previewUrl ?? (draft.kind === 'remove' ? null : savedUrl);

    // Aperçu local libéré dès qu'il est remplacé ou que la fenêtre se ferme.
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    // Le bouton cliqué disparaît : le focus revient sur « Ajouter une photo ».
    useEffect(() => {
        if (!refocusPick.current) return;
        refocusPick.current = false;
        pickRef.current?.focus();
    }, [draft]);

    const openPicker = () => {
        if (locked) return;
        inputRef.current?.click();
    };

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || locked) return;
        setPreparing(true);
        onPreparingChange(true);
        setError(null);
        setStatus('Préparation de la photo…');
        try {
            const image = await prepareImageForUpload(file, MENU_PHOTO_MAX_DIMENSION);
            if (!mounted.current) return;
            onDraftChange({ kind: 'set', image, previewUrl: URL.createObjectURL(image) });
            setStatus('Photo prête : elle sera enregistrée avec le plat.');
        } catch (err) {
            if (!mounted.current) return;
            setError(userMessage(err, "Cette photo n'a pas pu être préparée. Essayez avec une autre photo."));
            setStatus(''); // l'erreur est annoncée par son role="alert"
        } finally {
            if (mounted.current) {
                setPreparing(false);
                onPreparingChange(false);
            }
        }
    };

    const handleRemove = () => {
        if (locked) return;
        refocusPick.current = document.activeElement === removeRef.current;
        // Photo choisie mais pas encore envoyée : on revient à la photo enregistrée.
        onDraftChange(draft.kind === 'set' ? { kind: 'keep' } : { kind: 'remove' });
        setStatus(draft.kind === 'set' ? 'Photo choisie annulée.' : 'La photo sera retirée à la confirmation.');
    };

    const keepPhoto = () => {
        if (locked) return;
        refocusPick.current = true; // ce bouton disparaît
        onDraftChange({ kind: 'keep' });
        setStatus('Photo conservée.');
    };

    const removable = draft.kind === 'set' || (draft.kind === 'keep' && savedUrl !== null);
    return (
        <div>
            <span id="menu-photo-label" className="block text-xs font-bold text-gray-400 mb-1">
                Photo du plat <span className="normal-case font-normal">(facultative)</span>
            </span>
            <div className="flex items-center gap-4" role="group" aria-labelledby="menu-photo-label">
                <MenuItemThumb
                    restaurantId={restaurantId}
                    item={item}
                    previewUrl={previewUrl}
                    hidden={draft.kind === 'remove'}
                    className="w-24 h-24"
                />
                <div className="flex flex-col items-start gap-2">
                    <button
                        ref={pickRef}
                        type="button"
                        onClick={openPicker}
                        aria-disabled={locked}
                        className="px-3 py-2 bg-peach-100 text-brand-700 text-sm font-bold rounded-lg hover:bg-peach-200 active:scale-95 inline-flex items-center gap-2 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                    >
                        {preparing ? <SpinnerIcon className="w-4 h-4" /> : <CameraIcon className="w-4 h-4" />}
                        {shown ? 'Remplacer la photo' : 'Ajouter une photo'}
                    </button>
                    {removable && (
                        <button
                            ref={removeRef}
                            type="button"
                            onClick={handleRemove}
                            aria-disabled={locked}
                            className="px-3 py-1 text-sm text-red-600 font-bold rounded-lg hover:bg-red-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                        >
                            {draft.kind === 'set' ? 'Annuler cette photo' : 'Retirer la photo'}
                        </button>
                    )}
                    {draft.kind === 'remove' && (
                        <button
                            type="button"
                            onClick={keepPhoto}
                            aria-disabled={locked}
                            className="px-3 py-1 text-sm text-gray-500 font-bold rounded-lg hover:bg-gray-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                        >
                            Garder la photo
                        </button>
                    )}
                </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
                {busy && busy !== 'save'
                    ? 'Une photo de ce plat est en cours d\'envoi…'
                    : draft.kind === 'remove'
                        ? 'La photo sera retirée quand vous confirmerez.'
                        : 'JPG, PNG ou WebP — redimensionnée automatiquement, enregistrée quand vous confirmez.'}
            </p>
            <input ref={inputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} className="hidden" tabIndex={-1} onChange={handleFile} />
            {error && <p role="alert" className="text-sm text-red-600 mt-2">{error}</p>}
            <StatusRegion text={status} />
        </div>
    );
};
